'use client'

import { useTranslations } from 'next-intl'
import * as React from 'react'
import { ArrowRightIcon } from 'primitives/icons/arrows'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { Link } from '@repo/web-ui/components/link'
import { useRefinementList, useHits, useConfigure } from 'react-instantsearch'
import type { SearchProduct } from 'cf-menu/components/search/search.types'
import { useSearchParams } from 'next/navigation'
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'

import { Chip } from './chip'
import { useClubhousesGetDetailsQuery } from '@web/gen/clubhouses'
import { ProductCard } from '../inspiration/_components/product-card'

export function DigitalAssets({ slug }: { slug: string }) {
  const t = useTranslations('pages.clubhouse')
  const searchParams = useSearchParams()
  const { hits } = useHits<SearchProduct>({})
  const limitedHits = hits.slice(0, 12)

  const checkIfIsRefined = (value: string, items: RefinementListItem[]) =>
    items?.some(item => item.value === value && item.isRefined)

  const checkIfAllTypesAreRefined = (items: RefinementListItem[]) =>
    items?.every(item => item.isRefined) || items?.every(item => !item.isRefined)

  const { data: digitalAssetsFilters } = useClubhousesGetDetailsQuery(
    {
      input: { clubhouseSlug: slug },
    },
    {
      select: data =>
        data?.clubhousesGetDetails?.clubhouse?.digitalAssetsFilters?.map(item =>
          item.replace('type:', ''),
        ),
    },
  )

  const { items, refine } = useRefinementList({
    attribute: 'type',
  })

  const { data: clubhouse } = useClubhousesGetDetailsQuery(
    {
      input: { clubhouseSlug: slug || '' },
    },
    {
      select: data => data?.clubhousesGetDetails?.clubhouse,
    },
  )

  useConfigure({
    filters: digitalAssetsFilters
      ? digitalAssetsFilters.map(item => `type:'${item}'`).join(' OR ')
      : '',
    ...(clubhouse?.query ? { query: clubhouse.query } : {}),
  })

  const removeAllContentRefinements = () => {
    const newSearchParams = new URLSearchParams(searchParams.toString())
    newSearchParams.delete('type')
  }

  return (
    <section
      className="bg-cf-white rounded-4 flex h-full w-full flex-col justify-between border border-[#DADCE2] p-4 md:p-6"
      data-testid="recommended-digital-assets-container"
    >
      <div>
        <p className="text-20 font-bold">{t('digital_assets.title')}</p>
        <p className="text-14 mt-2">{t('digital_assets.subtitle')}</p>
        <div className="scroller no-scrollbar flex max-w-full flex-row items-center gap-2 overflow-x-auto whitespace-nowrap pt-4">
          <Chip
            text="All"
            onClick={removeAllContentRefinements}
            enabled={checkIfAllTypesAreRefined(items)}
          />
          {digitalAssetsFilters
            ?.toSorted((a, b) => a.localeCompare(b))
            .map(item => (
              <Chip
                key={item}
                text={item}
                onClick={() => refine(item)}
                enabled={checkIfIsRefined(item, items)}
              />
            ))}
        </div>
        <div className="mt-4 grid w-full grid-cols-2 lg:grid-cols-4 gap-4">
          {limitedHits.map(item => (
            <ProductCard key={item.objectID} item={item} />
          ))}
        </div>
      </div>
      <div>
        <div className=" my-4 w-full border-t border-[#DADCE2] px-4" />
        <Link
          href={`${AppRoutes.CLUBHOUSE}/${slug}/discussions`}
          className="flex w-full items-center justify-center text-cf-purple-900"
        >
          <p className="text-14 font-semibold">{t('digital_assets.show_all')}</p>
          <ArrowRightIcon className=" ml-1 size-4" />
        </Link>
      </div>
    </section>
  )
}
